import type { UseDataResult } from '@blueshift-ui/fetch/dist/types';

import useAxiosClient from '@blueshift-ui/fetch/dist/hooks/use-axios-client';
import useData from '@blueshift-ui/fetch/dist/hooks/use-data';

interface UnreadMessagesResponse {
  has_unread_messages: boolean;
  unread_message_count: number;
}

const selectUnreadCount = (response: UnreadMessagesResponse | undefined): number =>
  response?.unread_message_count ?? 0;

const REFETCH_INTERVAL_MS = 60000;
const STALE_TIME_MS = 10000;

/**
 * This hook is used to get the number of unread messages for the current user.
 *
 * Note: It uses polling to check for unread messages every minute.
 * A better approach in the future would be to use the Twilio API to get real-time updates.
 * It would be more efficient and would reduce the load on the server.
 */
function useUnreadMessagesCount({
  enabled = true,
}: { enabled?: boolean } = {}): UseDataResult<number> {
  const client = useAxiosClient();

  return useData<UnreadMessagesResponse, number>({
    queryKey: ['unreadMessages'],
    queryOptions: {
      enabled,
      meta: { client },
      refetchInterval: REFETCH_INTERVAL_MS,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      select: selectUnreadCount,
      staleTime: STALE_TIME_MS,
    },
    request: {
      url: '/vtwa/messenger/v1/users/has_unread_messages',
    },
  });
}

export default useUnreadMessagesCount;
